// ─────────────────────────────────────────────────────────────────────────────
// BlogCard.jsx — reusable blog/news card component
// Used in: NewsSection (home), BlogPage, BlogPostPage (related)
//
// Props:
//   post       {object}  — blog post object from blogPosts.js (required)
//   index      {number}  — position in list, used for animation delay (default: 0)
//   triggered  {boolean} — fade-in animation trigger from useFadeIn hook (default: true)
//   variant    {string}  — 'default' | 'compact'
//                          'default'  → full excerpt (5 lines), full padding
//                          'compact'  → shorter excerpt (3 lines), tighter layout
//                          (default: 'default')
//
// Usage:
//   import BlogCard from '@components/common/BlogCard'
//
//   // In NewsSection (home page):
//   <BlogCard post={post} index={i} triggered={vis} />
//
//   // In BlogPage grid:
//   <BlogCard post={post} index={i} triggered={vis} />
//
//   // In BlogPostPage related section (compact):
//   <BlogCard post={post} variant="compact" />
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react'
import { Link }     from 'react-router-dom'
import { formatDate } from '@data/blogPosts'

// Gradient fallbacks when no image
const BG_GRADIENTS = [
  'linear-gradient(135deg, #2d5c3e 0%, #3d7a55 100%)',
  'linear-gradient(135deg, #1e4d2f 0%, #2d6040 100%)',
  'linear-gradient(135deg, #163d25 0%, #255038 100%)',
  'linear-gradient(135deg, #0f3020 0%, #1e4d2f 100%)',
]

export default function BlogCard({
  post,
  index     = 0,
  triggered = true,
  variant   = 'default',
}) {
  const [hovered, setHovered] = useState(false)
  const isCompact = variant === 'compact'

  // Animation delay — caps at 6 to avoid long waits on large grids
  const delay = Math.min(index % 12, 6) * 0.06

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:       'flex',
        flexDirection: 'column',
        opacity:       triggered ? 1 : 0,
        transform:     triggered ? 'translateY(0)' : 'translateY(20px)',
        transition:    `opacity 0.45s ease ${delay}s, transform 0.45s ease ${delay}s`,
      }}
    >

      {/* ── Image with category overlay strip + chevron ── */}
      <div style={{
        position:    'relative',
        aspectRatio: '16/9',
        overflow:    'hidden',
        background:  BG_GRADIENTS[index % BG_GRADIENTS.length],
        flexShrink:  0,
      }}>
        {post.image && (
          <img
            src={post.image}
            alt={post.title}
            loading="lazy"
            style={{
              width:      '100%',
              height:     '100%',
              objectFit:  'cover',
              display:    'block',
              transform:  hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.4s ease',
            }}
          />
        )}

        {/* Category strip — MIAVIT style with > chevron */}
        <Link
          to={`/blog/${post.slug}`}
          style={{
            position:       'absolute',
            bottom:         0, left: 0, right: 0,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            padding:        '9px 14px',
            background:     hovered ? 'rgba(0,0,0,0.72)' : 'rgba(0,0,0,0.55)',
            fontFamily:     "'DM Sans', sans-serif",
            fontSize:       '13px',
            fontWeight:     400,
            color:          '#ffffff',
            textDecoration: 'none',
            transition:     'background 0.2s ease',
          }}
        >
          <span>{post.category}</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 3l5 5-5 5"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>
      </div>

      {/* ── Content ── */}
      <div style={{
        paddingTop: isCompact ? '14px' : '18px',
        flex:       1,
        display:    'flex',
        flexDirection: 'column',
      }}>

        {/* Green title — MIAVIT style */}
        <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
          <h3 style={{
            fontFamily:  "'DM Sans', sans-serif",
            fontSize:    isCompact ? '0.9375rem' : '1rem',
            fontWeight:  600,
            color:       hovered ? '#065e32' : '#007935',
            lineHeight:  1.35,
            margin:      '0 0 6px',
            transition:  'color 0.2s ease',
          }}>
            {post.title}
          </h3>
        </Link>

        {/* Bold black subtitle — author + readtime like MIAVIT */}
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize:   '12.5px',
          fontWeight: 700,
          color:      '#1a1a18',
          margin:     '0 0 10px',
          lineHeight: 1.4,
        }}>
          {post.author}
          {post.readTime && ` — ${post.readTime}`}
        </p>

        {/* Excerpt */}
        <p style={{
          fontFamily:          "'DM Sans', sans-serif",
          fontSize:            '13.5px',
          color:               '#55544e',
          lineHeight:          1.65,
          margin:              '0 0 16px',
          flex:                1,
          display:             '-webkit-box',
          WebkitLineClamp:     isCompact ? 3 : 5,
          WebkitBoxOrient:     'vertical',
          overflow:            'hidden',
        }}>
          {post.excerpt}
        </p>

        {/* Read more button — outlined, fills green on hover */}
        <div>
          <Link
            to={`/blog/${post.slug}`}
            style={{
              display:        'inline-flex',
              alignItems:     'center',
              fontFamily:     "'DM Sans', sans-serif",
              fontSize:       '13px',
              fontWeight:     600,
              color:          hovered ? '#ffffff' : '#007935',
              textDecoration: 'none',
              padding:        isCompact ? '6px 14px' : '7px 16px',
              border:         '1.5px solid #007935',
              background:     hovered ? '#007935' : 'transparent',
              transition:     'all 0.2s ease',
            }}
          >
            Read more ...
          </Link>
        </div>

      </div>
    </article>
  )
}